/*
 * ModulesConsole
 * ──────────────
 * Admin CMS for course modules.
 *
 * Two-pane layout:
 *   LEFT   list of every module with tier + search
 *   RIGHT  editor for the selected module with the following sections:
 *            • Metadata      — description, body/overview, min_tier
 *            • Video         — file upload (MP4/WEBM/MOV) or paste URL
 *            • Deck          — PDF upload or paste URL
 *            • Tutorial      — YouTube embed or Kajabi URL
 *            • Worksheets    — checklist of every worksheet in the catalog,
 *                              admin ticks which ones appear on the module
 *            • Tools         — checklist of financial tools attached to
 *                              the module
 */
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, formatError, API } from "../lib/api";
import { sanitizeVideoEmbed } from "../lib/sanitizeEmbed";
import { toast } from "sonner";
import {
  Save, Loader2, Search, Upload, Trash2, ExternalLink, FileText,
  FileVideo, Presentation, ClipboardList, Wrench, ChevronRight, X, Play, RefreshCw,
  CheckCircle2, Plus, Link as LinkIcon, Code2,
} from "lucide-react";

const NAV_SECTIONS = [
  { id: "meta",       label: "Overview",   icon: ClipboardList },
  { id: "video",      label: "Video",      icon: FileVideo },
  { id: "deck",       label: "Deck (PDF)", icon: Presentation },
  { id: "tutorial",   label: "Tutorial",   icon: ExternalLink },
  { id: "worksheets", label: "Worksheets", icon: FileText },
  { id: "tools",      label: "Tools",      icon: Wrench },
];

export default function ModulesConsole({ modules, reloadModules }) {
  const [selectedSlug, setSelectedSlug] = useState(modules[0]?.slug || "");
  const [query, setQuery] = useState("");
  const [section, setSection] = useState("meta");
  const [createOpen, setCreateOpen] = useState(false);

  // Per-slug video upload state, LIFTED here so it survives module tab
  // switches. Each entry: { file, fileName, fileSize, uploadId,
  // chunkSize, totalChunks, nextIndex, progress, status, error,
  // completedAt }. Status is one of "uploading" | "error" | "complete".
  // The chunk loop is driven by `runVideoChunkLoop` and reads the
  // authoritative current state from `uploadsRef` (a ref, not the state
  // snapshot the async closure captured), so switching modules mid-
  // upload does NOT wipe or duplicate progress.
  const [uploads, setUploads] = useState({});
  const uploadsRef = useRef(uploads);
  uploadsRef.current = uploads;

  const patchUpload = useCallback((slug, patch) => {
    setUploads((prev) => {
      const cur = prev[slug] || {};
      const merged = { ...cur, ...(typeof patch === "function" ? patch(cur) : patch) };
      return { ...prev, [slug]: merged };
    });
  }, []);

  const runVideoChunkLoop = useCallback(async (slug) => {
    const state0 = uploadsRef.current[slug];
    if (!state0 || !state0.file) return;
    const { file, uploadId, chunkSize, totalChunks } = state0;
    const total = file.size;
    let idx = state0.nextIndex;
    let sent = idx * chunkSize;
    try {
      while (idx < totalChunks) {
        // If the user cancelled (state removed) or a newer upload started
        // with a different uploadId, bail out silently.
        const live = uploadsRef.current[slug];
        if (!live || live.uploadId !== uploadId) return;
        const start = idx * chunkSize;
        const end = Math.min(start + chunkSize, total);
        const blob = file.slice(start, end);
        let attempt = 0;
        const chunkStartBytes = sent;
        while (true) {
          try {
            const fd = new FormData();
            fd.append("upload_id", uploadId);
            fd.append("index", String(idx));
            fd.append("chunk", blob, `chunk-${idx}`);
            await api.post(`/admin/modules/${slug}/upload-video/chunk`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
              onUploadProgress: (evt) => {
                const loaded = evt.loaded || 0;
                patchUpload(slug, {
                  progress: Math.min(100, Math.round(((chunkStartBytes + loaded) / total) * 100)),
                });
              },
            });
            break;
          } catch (err) {
            const st = err?.response?.status;
            if (st && st >= 400 && st < 500 && st !== 408) throw err;
            attempt += 1;
            if (attempt >= 3) throw err;
            await new Promise((r) => setTimeout(r, 400 * attempt));
          }
        }
        sent = end;
        idx += 1;
        const nextPatch = { nextIndex: idx, progress: Math.round((sent / total) * 100) };
        uploadsRef.current = {
          ...uploadsRef.current,
          [slug]: { ...(uploadsRef.current[slug] || {}), ...nextPatch },
        };
        patchUpload(slug, nextPatch);
      }
      const finFd = new FormData();
      finFd.append("upload_id", uploadId);
      const { data: final } = await api.post(
        `/admin/modules/${slug}/upload-video/complete`, finFd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const donePatch = {
        status: "complete", progress: 100, error: null,
        completedAt: Date.now(), resultUrl: final?.url,
      };
      uploadsRef.current = {
        ...uploadsRef.current,
        [slug]: { ...(uploadsRef.current[slug] || {}), ...donePatch },
      };
      patchUpload(slug, donePatch);
      toast.success(`Video uploaded for ${slug} — preview refreshed`);
      reloadModules();
    } catch (err) {
      const errPatch = { status: "error", error: formatError(err) };
      uploadsRef.current = {
        ...uploadsRef.current,
        [slug]: { ...(uploadsRef.current[slug] || {}), ...errPatch },
      };
      patchUpload(slug, errPatch);
      toast.error(`Upload failed for ${slug}: ${formatError(err)}`);
    }
  }, [patchUpload, reloadModules]);

  const startVideoUpload = useCallback(async (slug, file) => {
    try {
      const initFd = new FormData();
      initFd.append("filename", file.name || "");
      initFd.append("size", String(file.size || 0));
      initFd.append("mime", file.type || "");
      const { data: init } = await api.post(
        `/admin/modules/${slug}/upload-video/init`, initFd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const chunkSize = init.chunk_size || 8 * 1024 * 1024;
      const totalChunks = Math.max(1, Math.ceil(file.size / chunkSize));
      const initialState = {
        file,
        fileName: file.name,
        fileSize: file.size,
        uploadId: init.upload_id,
        chunkSize,
        totalChunks,
        nextIndex: 0,
        progress: 0,
        status: "uploading",
        error: null,
        completedAt: null,
      };
      // Prime the ref synchronously so the chunk loop below sees the
      // fresh state even before React commits `setUploads`. Without
      // this, `runVideoChunkLoop` occasionally read a stale ref and
      // bailed out at the `if (!state.file) return` guard.
      uploadsRef.current = { ...uploadsRef.current, [slug]: initialState };
      patchUpload(slug, initialState);
      runVideoChunkLoop(slug);
    } catch (err) {
      patchUpload(slug, { status: "error", error: formatError(err) });
      toast.error(formatError(err));
    }
  }, [patchUpload, runVideoChunkLoop]);

  const resumeVideoUpload = useCallback(async (slug) => {
    const state = uploadsRef.current[slug];
    if (!state || !state.file) {
      toast.error("Upload session lost — re-select the video file.");
      patchUpload(slug, { status: "error", error: "Upload session lost. Re-select the file." });
      return;
    }
    // Sync our client-side `nextIndex` with what the server actually
    // persisted, in case the last failure happened after the chunk
    // landed but before we heard back.
    let nextIndex = state.nextIndex;
    try {
      const r = await api.get(`/admin/modules/${slug}/upload-video/status/${state.uploadId}`);
      if (typeof r.data?.next_index === "number") nextIndex = r.data.next_index;
    } catch (_) { /* fall through with local nextIndex */ }
    const nextState = { ...state, nextIndex, status: "uploading", error: null };
    uploadsRef.current = { ...uploadsRef.current, [slug]: nextState };
    patchUpload(slug, { nextIndex, status: "uploading", error: null });
    runVideoChunkLoop(slug);
  }, [patchUpload, runVideoChunkLoop]);

  const dismissUpload = useCallback((slug) => {
    setUploads((prev) => {
      const next = { ...prev };
      delete next[slug];
      return next;
    });
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return modules;
    const q = query.toLowerCase();
    return modules.filter((m) =>
      (m.name || "").toLowerCase().includes(q) ||
      (m.slug || "").toLowerCase().includes(q)
    );
  }, [modules, query]);

  useEffect(() => {
    if (!selectedSlug && modules[0]?.slug) setSelectedSlug(modules[0].slug);
  }, [modules, selectedSlug]);

  const selected = modules.find((m) => m.slug === selectedSlug);

  return (
    <div className="grid lg:grid-cols-[300px_1fr] gap-6" data-testid="admin-modules-cms">
      {/* LEFT: module list */}
      <div className="card-elevated overflow-hidden h-fit">
        <div className="p-3 border-b border-shell-border">
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            data-testid="admin-modules-new-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary text-white text-sm font-semibold px-3 py-2 hover:brightness-110 transition"
          >
            <Plus className="w-4 h-4" /> New module
          </button>
          <div className="relative mt-2">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search modules…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-shell-border bg-white focus:outline-none focus:border-primary/40"
              data-testid="admin-modules-search"
            />
          </div>
          <p className="mt-2 text-[10px] uppercase tracking-widest text-muted-foreground">
            {filtered.length} of {modules.length}
          </p>
        </div>
        <ul className="max-h-[75vh] overflow-auto" data-testid="admin-modules-list">
          {filtered.map((m) => {
            const active = m.slug === selectedSlug;
            const hasVideo = !!m.video_url;
            const hasDeck = !!m.deck_url;
            const upload = uploads[m.slug];
            return (
              <li key={m.slug}>
                <button
                  type="button"
                  onClick={() => setSelectedSlug(m.slug)}
                  data-testid={`admin-module-pick-${m.slug}`}
                  className={[
                    "w-full text-left px-3 py-3 border-l-4 transition flex items-start gap-2",
                    active
                      ? "border-primary bg-primary/5"
                      : "border-transparent hover:bg-muted/50",
                  ].join(" ")}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{({1:"Starter",2:"Growth",3:"Full Access"})[m.min_tier] || "\u00A0"}</p>
                    <p className="font-semibold text-sm truncate">{m.name}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      {hasVideo && <Dot label="V" title="Video uploaded" testid={`admin-module-video-indicator-${m.slug}`} />}
                      {hasDeck && <Dot label="D" title="Deck uploaded" testid={`admin-module-deck-indicator-${m.slug}`} />}
                      {(m.tool_slugs || []).length > 0 && <Dot label="T" title={`${m.tool_slugs.length} tools`} testid={`admin-module-tools-indicator-${m.slug}`} />}
                      {upload?.status === "uploading" && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-primary/15 text-primary text-[9px] font-bold px-1.5 py-0.5"
                          data-testid={`admin-module-upload-badge-${m.slug}`}
                          title={`Video upload: ${upload.progress || 0}%`}
                        >
                          <Loader2 className="w-2.5 h-2.5 animate-spin" />
                          {upload.progress || 0}%
                        </span>
                      )}
                      {upload?.status === "error" && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-destructive/15 text-destructive text-[9px] font-bold px-1.5 py-0.5"
                          data-testid={`admin-module-upload-error-badge-${m.slug}`}
                          title={upload.error || "Upload paused"}
                        >
                          Paused
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className={["w-4 h-4 mt-2 flex-shrink-0", active ? "text-primary" : "text-muted-foreground"].join(" ")} />
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* RIGHT: editor */}
      <div className="min-w-0">
        {!selected ? (
          <div className="card-elevated p-10 text-center text-muted-foreground">
            Pick a module from the list to edit.
          </div>
        ) : (
          <ModuleEditor
            module={selected}
            reload={reloadModules}
            section={section}
            setSection={setSection}
            uploadState={uploads[selectedSlug]}
            startVideoUpload={startVideoUpload}
            resumeVideoUpload={resumeVideoUpload}
            dismissUpload={dismissUpload}
          />
        )}
      </div>

      {createOpen && (
        <NewModuleDialog
          onClose={() => setCreateOpen(false)}
          onCreated={async (created) => {
            await reloadModules();
            setSelectedSlug(created.slug);
            setSection("meta");
            setCreateOpen(false);
          }}
        />
      )}
    </div>
  );
}


/**
 * Minimal “new module” form — creates a stub the admin can then flesh
 * out with video, deck, body, tools, etc. via the existing editor.
 * Slug is auto-derived from the name but the admin can override it.
 */
function NewModuleDialog({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [minTier, setMinTier] = useState(0);
  const [track, setTrack] = useState("core");
  const [lesson, setLesson] = useState("");
  const [saving, setSaving] = useState(false);

  const autoSlug = (s) =>
    s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name is required"); return; }
    const finalSlug = (slug.trim() || autoSlug(name)).replace(/^-+|-+$/g, "");
    if (!finalSlug) { toast.error("Slug is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/modules", {
        slug: finalSlug,
        name: name.trim(),
        description: description.trim(),
        min_tier: Number(minTier) || 0,
        track: track.trim() || "core",
        lesson: lesson.trim(),
      });
      toast.success("Module created");
      await onCreated(data.module);
    } catch (err) {
      toast.error(formatError(err) || "Couldn’t create module");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="admin-modules-new-dialog"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-shell-border overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-shell-border">
          <h3 className="font-display text-lg">New module</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md hover:bg-muted"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Name" required>
            <input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (!slugTouched) setSlug(autoSlug(e.target.value));
              }}
              data-testid="admin-modules-new-name"
              placeholder="Module 12: Retention & Loyalty"
              className="w-full text-sm rounded-lg border border-shell-border px-3 py-2 focus:outline-none focus:border-primary/40"
              autoFocus
            />
          </Field>
          <Field label="Slug" required
                 hint="URL fragment. Lowercase letters, numbers, hyphens.">
            <input
              value={slug}
              onChange={(e) => { setSlugTouched(true); setSlug(e.target.value); }}
              data-testid="admin-modules-new-slug"
              placeholder="retention-and-loyalty"
              className="w-full text-sm font-mono rounded-lg border border-shell-border px-3 py-2 focus:outline-none focus:border-primary/40"
            />
          </Field>
          <Field label="Short description">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              data-testid="admin-modules-new-description"
              placeholder="What founders will learn in one sentence."
              className="w-full text-sm rounded-lg border border-shell-border px-3 py-2 focus:outline-none focus:border-primary/40"
            />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Min plan" hint="0 free · 1 core · 2 pro">
              <input
                type="number" min={0} max={5}
                value={minTier}
                onChange={(e) => setMinTier(e.target.value)}
                data-testid="admin-modules-new-min-tier"
                className="w-full text-sm rounded-lg border border-shell-border px-3 py-2"
              />
            </Field>
            <Field label="Track">
              <select
                value={track}
                onChange={(e) => setTrack(e.target.value)}
                data-testid="admin-modules-new-track"
                className="w-full text-sm rounded-lg border border-shell-border px-3 py-2 bg-white"
              >
                <option value="core">core</option>
                <option value="advanced">advanced</option>
                <option value="pro">pro</option>
                <option value="elective">elective</option>
              </select>
            </Field>
            <Field label="Lesson #">
              <input
                value={lesson}
                onChange={(e) => setLesson(e.target.value)}
                data-testid="admin-modules-new-lesson"
                placeholder="12"
                className="w-full text-sm rounded-lg border border-shell-border px-3 py-2"
              />
            </Field>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Video, deck, worksheets, and tools can be added after creation from the module editor.
          </p>
        </div>
        <div className="px-5 py-3 border-t border-shell-border flex items-center justify-end gap-2 bg-muted/30">
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-3 py-1.5 rounded-lg text-muted-foreground hover:text-shell-foreground"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="admin-modules-new-submit"
            className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg bg-primary text-white font-semibold hover:brightness-110 disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create module
          </button>
        </div>
      </form>
    </div>
  );
}


function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-widest text-muted-foreground mb-1">
        {label}{required && <span className="text-primary"> *</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-[10px] text-muted-foreground">{hint}</span>}
    </label>
  );
}

function Dot({ label, title, testid }) {
  return (
    <span
      title={title}
      data-testid={testid}
      className="inline-flex items-center justify-center rounded-full bg-primary/15 text-primary text-[9px] font-bold w-4 h-4"
    >
      {label}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
 * Single-module editor
 * ═══════════════════════════════════════════════════════════════════════ */
function ModuleEditor({ module, reload, section, setSection, uploadState, startVideoUpload, resumeVideoUpload, dismissUpload }) {
  const [draft, setDraft] = useState(module);
  const [saving, setSaving] = useState(false);
  const [worksheets, setWorksheets] = useState([]);
  const [tools, setTools] = useState([]);
  const [dirty, setDirty] = useState(false);

  // reset when module changes
  useEffect(() => { setDraft(module); setDirty(false); }, [module]);

  // Load worksheets + tools for the sidebar checklists
  const loadWorksheets = useCallback(
    () => api.get("/admin/worksheets").then((r) => setWorksheets(r.data || [])).catch(() => {}),
    [],
  );
  useEffect(() => {
    loadWorksheets();
    api.get("/tools").then((r) => setTools(r.data || [])).catch(() => {});
  }, [loadWorksheets]);

  const set = (k, v) => { setDraft((d) => ({ ...d, [k]: v })); setDirty(true); };

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await api.post(`/admin/modules/${module.slug}`, {
        tutorial_url: draft.tutorial_url || "",
        kajabi_url: draft.kajabi_url || "",
        video_url: draft.video_url || "",
        deck_url: draft.deck_url || "",
        body: draft.body || "",
        description: draft.description || "",
        min_tier: Number(draft.min_tier) || 1,
        tool_slugs: draft.tool_slugs || [],
        // Admin-editable metadata that flows into ModuleDetail's summary.
        // Empty string clears the field so admins can reset to defaults.
        lesson: draft.lesson ?? "",
        track:  draft.track  ?? "",
      });
      toast.success("Module saved");
      setDirty(false);
      reload();
    } catch (err) {
      toast.error(formatError(err));
    } finally { setSaving(false); }
  }, [module.slug, draft, reload]);

  // Handler: assign worksheet to this module (mutates via /admin/worksheets/:id PUT)
  const toggleWorksheet = async (ws) => {
    const isOn = (ws.module_slugs || []).includes(module.slug);
    const next = isOn
      ? (ws.module_slugs || []).filter((s) => s !== module.slug)
      : [...(ws.module_slugs || []), module.slug];
    try {
      const r = await api.put(`/admin/worksheets/${ws.id}`, { module_slugs: next });
      setWorksheets((list) => list.map((w) => w.id === ws.id ? r.data : w));
      toast.success(isOn ? "Removed from module" : "Added to module");
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="card-elevated overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-shell-border flex items-center gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-primary font-bold">Module editor</p>
          <h2 className="font-display text-2xl mt-0.5 truncate">{draft.name || module.name}</h2>
          <p className="text-xs text-muted-foreground">Slug: <code>{module.slug}</code></p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Link
            to={`/modules/${module.slug}`}
            target="_blank"
            className="text-xs text-muted-foreground hover:text-primary font-semibold inline-flex items-center gap-1"
          >
            View page <ExternalLink className="w-3 h-3" />
          </Link>
          <button
            type="button"
            onClick={save}
            disabled={saving || !dirty}
            data-testid={`admin-module-save-${module.slug}`}
            className="btn-primary text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </button>
        </div>
      </div>

      {/* Section tabs */}
      <div className="flex flex-wrap gap-1 px-6 pt-3 border-b border-shell-border/60">
        {NAV_SECTIONS.map((s) => {
          const active = section === s.id;
          const Icon = s.icon;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setSection(s.id)}
              data-testid={`admin-module-section-${s.id}`}
              className={[
                "inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 -mb-px transition",
                active
                  ? "border-primary text-primary bg-primary/5"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              ].join(" ")}
            >
              <Icon className="w-3.5 h-3.5" /> {s.label}
            </button>
          );
        })}
      </div>

      {/* Body */}
      <div className="p-6">
        {section === "meta" && <SectionMeta draft={draft} set={set} slug={module.slug} />}
        {section === "video" && (
          <SectionAsset
            key={`video-${module.slug}`}
            slug={module.slug}
            kind="video"
            currentUrl={draft.video_url}
            onUploaded={(url) => { set("video_url", url); }}
            onClear={() => set("video_url", "")}
            accept=".mp4,.webm,.mov"
            allowedText="MP4 · WEBM · MOV"
            onSaved={reload}
            uploadState={uploadState}
            startVideoUpload={startVideoUpload}
            resumeVideoUpload={resumeVideoUpload}
            dismissUpload={dismissUpload}
          />
        )}
        {section === "deck" && (
          <SectionAsset
            key={`deck-${module.slug}`}
            slug={module.slug}
            kind="deck"
            currentUrl={draft.deck_url}
            onUploaded={(url) => { set("deck_url", url); }}
            onClear={() => set("deck_url", "")}
            accept=".pdf"
            allowedText="PDF"
            onSaved={reload}
          />
        )}
        {section === "tutorial" && <SectionTutorial draft={draft} set={set} />}
        {section === "worksheets" && (
          <SectionWorksheets
            moduleSlug={module.slug}
            worksheets={worksheets}
            onToggle={toggleWorksheet}
            onReload={loadWorksheets}
          />
        )}
        {section === "tools" && (
          <SectionTools
            tools={tools}
            selected={draft.tool_slugs || []}
            onToggle={(slug) => {
              const cur = draft.tool_slugs || [];
              set("tool_slugs", cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug]);
            }}
          />
        )}
      </div>
    </div>
  );
}

/* ── Section: Meta ─────────────────────────────────────────────────── */
function SectionMeta({ draft, set, slug }) {
  return (
    <div className="grid gap-4 max-w-3xl">
      <label className="grid gap-1">
        <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Short description</span>
        <input
          value={draft.description || ""}
          onChange={(e) => set("description", e.target.value)}
          data-testid="admin-module-description"
          className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40"
        />
      </label>

      <div className="grid gap-3 md:grid-cols-2">
        <label className="grid gap-1">
          <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Lesson</span>
          <input
            value={draft.lesson || ""}
            onChange={(e) => set("lesson", e.target.value)}
            data-testid="admin-module-lesson"
            placeholder="e.g. Lesson 3 · Positioning your brand"
            className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40"
          />
        </label>
        <label className="grid gap-1">
          <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Track</span>
          <input
            value={draft.track || ""}
            onChange={(e) => set("track", e.target.value)}
            data-testid="admin-module-track"
            placeholder="core · bonus · Foundations · Growth"
            className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40"
          />
        </label>
      </div>

      <label className="grid gap-1">
        <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Body / overview (Markdown supported)</span>
        <textarea
          rows={8}
          value={draft.body || ""}
          onChange={(e) => set("body", e.target.value)}
          data-testid="admin-module-body"
          className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40 resize-y font-mono"
        />
      </label>

      <label className="grid gap-1 w-40">
        <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Min plan</span>
        <select
          value={draft.min_tier || 1}
          onChange={(e) => set("min_tier", Number(e.target.value))}
          data-testid="admin-module-min-tier"
          className="rounded-lg border border-shell-border px-3 py-2 text-sm bg-white focus:outline-none focus:border-primary/40"
        >
          <option value={0}>Free</option>
          <option value={1}>Founder</option>
          <option value={2}>Pro</option>
        </select>
      </label>
    </div>
  );
}

/* ── Section: Video / Deck (file-upload w/ URL fallback) ─────────────── */
function SectionAsset({
  slug, kind, currentUrl, onUploaded, onClear, accept, allowedText, onSaved,
  uploadState, startVideoUpload, resumeVideoUpload, dismissUpload,
}) {
  // Deck uploads stay single-shot (small PDF payloads) so they keep the
  // component-local `uploading` / `progress` state. Video uploads read
  // from the LIFTED `uploadState` prop so progress + status survive
  // module tab switches.
  const [deckUploading, setDeckUploading] = useState(false);
  const [deckProgress, setDeckProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [deckBlobUrl, setDeckBlobUrl] = useState(null);
  const [deckError, setDeckError] = useState(false);
  const inputRef = useRef(null);
  const isVideo = kind === "video";
  const isDeck = kind === "deck";

  // Video-source picker (video kind only). Preselects the tab that
  // matches the currently-stored value so admins land on the correct
  // input for the existing source — same UX as Intro Section.
  //   • Uploaded file  → /api/modules/{slug}/asset/…   → "upload"
  //   • Vimeo/YT embed → raw `<div>…<iframe>` HTML     → "vimeo"
  //   • Everything else (YouTube share URL, direct MP4) → "url"
  //   • Empty                                          → "upload"
  const guessVideoTab = (v) => {
    const raw = String(v || "").trim();
    if (!raw) return "upload";
    if (raw.startsWith("<")) return "vimeo";
    if (raw.startsWith(`/api/modules/${slug}/asset/`)) return "upload";
    return "url";
  };
  const [videoTab, setVideoTab] = useState(() => guessVideoTab(currentUrl));
  // Re-sync tab whenever the stored value's shape changes (module
  // switch, remote reload, paste replaces upload, etc.). Same guard
  // pattern VideoSourcePicker uses for intros.
  const guessedVideoTab = useMemo(() => guessVideoTab(currentUrl), [currentUrl, slug]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setVideoTab(guessedVideoTab); }, [guessedVideoTab]);

  // Effective state for the current section: video reads from uploadState,
  // deck reads from local state. Only one of them is exercised per
  // instance thanks to the `key={kind-${slug}}` on the parent.
  const videoUploading = uploadState?.status === "uploading";
  const videoErrored   = uploadState?.status === "error";
  const uploading      = isVideo ? videoUploading : deckUploading;
  const progress       = isVideo ? (uploadState?.progress || 0) : deckProgress;

  // Load PDF previews via authenticated fetch → blob URL. iframes don't reliably
  // send the SameSite=None; Partitioned auth cookie for their initial load, so
  // a direct iframe src to /api/... 401s. Video previews are unaffected because
  // <video> elements handle credentials fine.
  useEffect(() => {
    if (!isDeck || !currentUrl) { setDeckBlobUrl(null); setDeckError(false); return; }
    // External URLs (Cloudflare, S3, etc.) render fine — skip the blob dance.
    if (currentUrl.startsWith("http")) { setDeckBlobUrl(currentUrl); setDeckError(false); return; }
    let cancelled = false;
    let created = null;
    setDeckError(false);
    setDeckBlobUrl(null);
    const fullUrl = `${API}${currentUrl.replace(/^\/api/, "")}`;
    fetch(fullUrl, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
      .then((blob) => {
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setDeckBlobUrl(created);
      })
      .catch(() => { if (!cancelled) setDeckError(true); });
    return () => { cancelled = true; if (created) URL.revokeObjectURL(created); };
  }, [isDeck, currentUrl]);

  const doUpload = async (file) => {
    if (!file) return;
    // Validate extension against the accept list (defensive — the input
    // element also enforces this, but drag-drop bypasses it).
    const allowedExts = (accept || "").split(",").map((s) => s.trim().toLowerCase());
    const fileExt = ("." + (file.name.split(".").pop() || "")).toLowerCase();
    if (allowedExts.length && !allowedExts.includes(fileExt)) {
      toast.error(`Unsupported file type: ${fileExt}. Allowed: ${allowedText}`);
      return;
    }
    if (isVideo) {
      // Fire-and-forget: the LIFTED upload manager owns the async loop
      // so the progress bar keeps ticking even when this section
      // unmounts on a module tab switch.
      startVideoUpload(slug, file);
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    // Deck path stays single-shot.
    setDeckUploading(true);
    setDeckProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/modules/${slug}/upload-${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setDeckProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      const url = r.data?.url;
      if (!url) throw new Error("No URL returned");
      onUploaded(url);
      if (typeof onSaved === "function") onSaved();
      toast.success(`Deck uploaded and saved — reload /modules/${slug} to see it in the reading tab.`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setDeckUploading(false);
      setDeckProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    const file = e.dataTransfer?.files?.[0];
    if (file) doUpload(file);
  };

  // For video, previewUrl is directly what we render. For decks, we render
  // deckBlobUrl (a blob:) instead so the browser's PDF viewer works reliably.
  // A `?t=<completedAt>` cache-buster is appended after each successful video
  // upload so browsers re-fetch the fresh bytes when the server URL is unchanged.
  //
  // Build a preview URL that either points at the CDN asset or is
  // passed through as-is for external / embed sources. Order matters:
  //   1. Raw Vimeo/Wistia embed HTML paste (starts with `<`) → keep
  //      verbatim; the preview block below renders it via
  //      `dangerouslySetInnerHTML`. If we prefix the backend origin
  //      here it becomes `https://…<div><iframe>…` which the fallback
  //      branch then loads as an `<iframe src>` — that's the black
  //      screen founders were seeing after pasting an embed code.
  //   2. Absolute URL (external video / hosted deck) → passthrough.
  //   3. Relative canonical `/api/…` pointer → prefix with the
  //      backend origin so the browser can fetch it. Append a
  //      cache-buster after a completed upload so the freshly-swapped
  //      MP4 shows immediately.
  const previewUrl = useMemo(() => {
    const raw = String(currentUrl || "").trim();
    if (!raw) return "";
    if (raw.startsWith("<")) return raw;
    const base = raw.startsWith("http")
      ? raw
      : `${process.env.REACT_APP_BACKEND_URL}${raw}`;
    if (isVideo && uploadState?.completedAt && !raw.startsWith("http")) {
      const sep = base.includes("?") ? "&" : "?";
      return `${base}${sep}t=${uploadState.completedAt}`;
    }
    return base;
  }, [currentUrl, isVideo, uploadState?.completedAt]);

  return (
    <div className="grid gap-4 max-w-3xl">
      {currentUrl && (
        <div className="rounded-xl overflow-hidden bg-black/95 aspect-video max-w-2xl relative">
          {isVideo ? (
            String(previewUrl || "").trim().startsWith("<") ? (
              // Raw embed HTML paste (Vimeo Embed Code option) —
              // render verbatim so the admin sees exactly what
              // members will see. Aspect wrapper drops to auto so
              // Vimeo's padding-ratio isn't fighting our 16:9 box.
              <div
                key={previewUrl}
                className="w-full h-full aspect-auto [&_iframe]:w-full [&_iframe]:h-full [&_iframe]:absolute [&_iframe]:top-0 [&_iframe]:left-0"
                data-testid={`admin-module-preview-${kind}-embed`}
                dangerouslySetInnerHTML={{ __html: sanitizeVideoEmbed(previewUrl) }}
              />
            ) : /youtube\.com|youtu\.be|vimeo\.com/.test(previewUrl) ? (
              <iframe key={previewUrl} src={previewUrl} allow="autoplay; fullscreen" className="w-full h-full" />
            ) : (
              <video key={previewUrl} src={previewUrl} controls className="w-full h-full"
                     data-testid={`admin-module-preview-${kind}`} />
            )
          ) : deckError ? (
            <div className="w-full h-full flex flex-col items-center justify-center text-white/80 text-xs gap-2 p-4 text-center">
              <p>Preview failed to load. The file was uploaded but couldn't be rendered.</p>
              <a href={previewUrl} target="_blank" rel="noreferrer" className="text-primary underline">Open in new tab</a>
            </div>
          ) : deckBlobUrl ? (
            <iframe src={deckBlobUrl} title="Deck preview" className="w-full h-full bg-white"
                    data-testid={`admin-module-preview-${kind}`} />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/80 text-xs">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading deck…
            </div>
          )}
        </div>
      )}

      {/* Resume banner for a stalled video upload — only rendered when the
          lifted `uploadState` says this slug's upload errored. */}
      {isVideo && videoErrored && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3"
             data-testid={`admin-module-upload-error-${slug}`}>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-destructive uppercase tracking-widest">Upload paused</p>
            <p className="text-sm mt-0.5 truncate">{uploadState?.fileName || "video"}</p>
            <p className="text-[11px] text-muted-foreground mt-1">
              Stopped at {uploadState?.progress || 0}%. {uploadState?.error || "Network hiccup."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => resumeVideoUpload(slug)}
              disabled={!uploadState?.file}
              data-testid={`admin-module-upload-resume-${slug}`}
              className="btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
              title={uploadState?.file ? "" : "Session lost — re-select the file to start again."}
            >
              <RefreshCw className="w-3.5 h-3.5" /> Resume upload
            </button>
            <button
              type="button"
              onClick={() => dismissUpload(slug)}
              data-testid={`admin-module-upload-dismiss-${slug}`}
              className="text-xs font-semibold text-muted-foreground hover:text-destructive"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Video source picker — three-tab control shown ONLY for videos
          (decks are single-file uploads). The tab auto-selects to
          match the currently-stored source (upload / URL / embed)
          so admins land on the right control for the existing video
          instead of scrolling past three simultaneous inputs. */}
      {isVideo && (
        <div
          role="tablist"
          aria-label="Video source"
          data-testid={`admin-module-${kind}-tabs`}
          className="inline-flex items-center gap-1 rounded-full border border-shell-border bg-white p-1 text-[11px] font-semibold"
        >
          {[
            { id: "upload", label: "Video Upload",     icon: Upload },
            { id: "url",    label: "Video URL",        icon: LinkIcon },
            { id: "vimeo",  label: "Vimeo Embed Code", icon: Code2 },
          ].map((t) => {
            const Icon = t.icon;
            const active = videoTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setVideoTab(t.id)}
                data-testid={`admin-module-${kind}-tab-${t.id}`}
                className={[
                  "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 transition",
                  active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
                ].join(" ")}
              >
                <Icon className="w-3 h-3" />
                {t.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Drag-and-drop zone — click to open the file picker OR drop a file.
          Rendered for decks always, and for the video Upload tab only. */}
      {(isDeck || videoTab === "upload") && (
      <div
        onClick={() => !uploading && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        data-testid={`admin-module-dropzone-${kind}`}
        className={"cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-colors " +
          (dragOver
            ? "border-primary bg-primary/5"
            : uploading
              ? "border-shell-border bg-muted/40 cursor-wait"
              : "border-shell-border hover:border-primary/50 hover:bg-muted/30")
        }
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-2" data-testid={`admin-module-upload-progress-${kind}`}>
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
            <p className="text-sm font-semibold">
              Uploading {isVideo && uploadState?.fileName ? uploadState.fileName : ""}… {progress}%
            </p>
            <div className="w-full max-w-xs h-1.5 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
            </div>
            {isVideo && (
              <p className="text-[11px] text-muted-foreground">
                You can switch to another module — this upload keeps running in the background.
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className={"w-6 h-6 " + (dragOver ? "text-primary" : "text-muted-foreground")} />
            <p className="text-sm font-semibold">
              {dragOver
                ? `Drop to upload ${isVideo ? "video" : "deck"}`
                : currentUrl
                  ? `Drag a file here to replace, or click to browse`
                  : `Drag a file here to upload, or click to browse`}
            </p>
            <p className="text-[11px] text-muted-foreground">{allowedText}</p>
          </div>
        )}
      </div>
      )}

      {/* Hidden file input driven by the drop zone above. */}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        data-testid={`admin-module-upload-${kind}`}
        onChange={(e) => doUpload(e.target.files?.[0])}
      />

      {currentUrl && (
        <div className="flex items-center gap-3 flex-wrap" data-testid={`admin-module-asset-actions-${kind}`}>
          {/* Only show the "Auto-saved" pill for URLs that live on our own
              storage endpoint (`/api/modules/{slug}/asset/...`) — those are
              persisted at upload time. Externally-pasted URLs are draft-only
              until the admin clicks Save. */}
          {currentUrl.startsWith(`/api/modules/${slug}/asset/`) && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest"
              title={`This ${isVideo ? "video" : "deck"} is stored and live for members right now. Uploads are auto-saved — no Save click needed.`}
              data-testid={`admin-module-autosaved-${kind}`}
            >
              <CheckCircle2 className="w-3 h-3" /> Auto-saved · live for members
            </span>
          )}
          <a
            href={currentUrl.startsWith("http") ? currentUrl : `${API}${currentUrl.replace(/^\/api/, "")}`}
            target="_blank"
            rel="noreferrer"
            className="text-xs font-semibold text-primary hover:text-primary/80 inline-flex items-center gap-1"
            data-testid={`admin-module-preview-newtab-${kind}`}
            title={`Open ${isVideo ? "video" : "deck"} in a new tab to verify what members will see`}
          >
            <ExternalLink className="w-3 h-3" /> Preview {isVideo ? "video" : "deck"} in new tab
          </a>
          <button
            type="button"
            onClick={onClear}
            className="text-xs font-semibold text-muted-foreground hover:text-destructive inline-flex items-center gap-1"
            data-testid={`admin-module-clear-${kind}`}
          >
            <Trash2 className="w-3 h-3" /> Remove {isVideo ? "video" : "deck"}
          </button>
        </div>
      )}

      {isDeck && <DeckHistoryPanel slug={slug} onSaved={onSaved} refreshKey={currentUrl} />}

      {(isDeck || videoTab === "url") && (
      <label className="grid gap-1">
        <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
          {isDeck ? "Or paste an external URL" : "Video URL"}
        </span>
        <input
          value={(currentUrl || "").trim().startsWith("<") ? "" : (currentUrl || "")}
          onChange={(e) => onUploaded(e.target.value)}
          placeholder={isVideo ? "https://youtu.be/... or https://cdn.example.com/video.mp4" : "https://.../deck.pdf"}
          data-testid={`admin-module-${kind}-url`}
          className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40"
        />
      </label>
      )}

      {/*
        Vimeo Embed Code — third source option for module videos only.
        Admin pastes the raw `<div>…<iframe>…</iframe>…<script>` HTML
        from Vimeo → Share → Embed and we persist it VERBATIM in the
        `video_url` field. No src extraction — the outer `<div>` carries
        the 16:9 padding-ratio wrapper and any `<script>` powers Vimeo
        chapters / analytics; both would break if we stripped them.
        `ModuleDetail.jsx` detects a leading `<` via `normalizeVideoUrl`
        and drops the HTML into `dangerouslySetInnerHTML` inside a
        responsive card.
      */}
      {isVideo && videoTab === "vimeo" && (
        <label className="grid gap-1">
          <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
            Or paste a Vimeo embed code (saved verbatim)
          </span>
          <textarea
            rows={5}
            value={(currentUrl || "").trim().startsWith("<") ? currentUrl : ""}
            onChange={(e) => onUploaded(e.target.value)}
            placeholder={`<div style="padding:56.25% 0 0 0;position:relative;"><iframe src="https://player.vimeo.com/video/…" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe></div>`}
            data-testid={`admin-module-${kind}-vimeo`}
            className="rounded-lg border border-shell-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary/40"
          />
          <span className="text-[11px] text-muted-foreground">
            Paste the <b>complete</b> HTML from Vimeo → Share → Embed (including the outer{" "}
            <code>&lt;div&gt;</code> and any <code>&lt;script&gt;</code>). It's saved verbatim
            and rendered exactly as pasted.
          </span>
        </label>
      )}
    </div>
  );
}

/* ── Deck version history + rollback ────────────────────────────────
   Backend keeps an append-only `deck_history` array on every module.
   Each row is `{storage_path, content_type, version_id, uploaded_at, size}`
   captured right before an overwrite. The panel lists them and lets
   the admin rollback with one click — the historical bytes get
   copied back to the canonical `/api/modules/{slug}/asset/deck.pdf`
   path so every existing member link keeps resolving.
*/
function DeckHistoryPanel({ slug, onSaved, refreshKey }) {
  const [history, setHistory] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rollingBack, setRollingBack] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/modules/${slug}/deck-history`);
      setCurrent(r.data?.current || null);
      setHistory(Array.isArray(r.data?.history) ? r.data.history : []);
    } catch (err) {
      // A 404 or 401 here is non-fatal — the editor is still usable
      // without history. Log to console for admin debugging.
      // eslint-disable-next-line no-console
      console.warn("[DeckHistoryPanel] load failed:", formatError(err));
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const rollback = async (row) => {
    if (!row?.storage_path) return;
    if (!window.confirm(`Roll deck back to the version uploaded ${row.uploaded_at || "earlier"}? Members will see the older file immediately.`)) return;
    setRollingBack(row.storage_path);
    try {
      await api.post(`/admin/modules/${slug}/deck-rollback`, { storage_path: row.storage_path });
      toast.success("Deck rolled back. Members are now seeing the older version.");
      if (typeof onSaved === "function") onSaved();
      await load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setRollingBack(null);
    }
  };

  if (loading && !history.length && !current) return null;
  if (!history.length) return null;

  return (
    <div className="rounded-xl border border-shell-border bg-muted/30 p-4" data-testid="admin-module-deck-history">
      <div className="flex items-center gap-2 mb-3">
        <RefreshCw className="w-3.5 h-3.5 text-muted-foreground" />
        <h4 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
          Deck version history · {history.length} prior upload{history.length === 1 ? "" : "s"}
        </h4>
      </div>
      <ul className="grid gap-2">
        {history.slice().reverse().map((row, i) => (
          <li
            key={row.storage_path || i}
            className="flex items-center gap-3 rounded-lg border border-shell-border bg-white px-3 py-2"
            data-testid={`admin-module-deck-history-row-${i}`}
          >
            <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold truncate">
                {row.uploaded_at ? new Date(row.uploaded_at).toLocaleString() : "Older upload"}
              </p>
              <p className="text-[10px] text-muted-foreground truncate">
                {row.size ? `${(row.size / 1024 / 1024).toFixed(2)} MB · ` : ""}
                {row.storage_path?.split("/").pop() || row.storage_path}
              </p>
            </div>
            <button
              type="button"
              onClick={() => rollback(row)}
              disabled={rollingBack === row.storage_path}
              className="text-xs font-semibold text-primary hover:text-primary/80 inline-flex items-center gap-1 disabled:opacity-50"
              data-testid={`admin-module-deck-rollback-${i}`}
              title="Restore this version as the current deck"
            >
              {rollingBack === row.storage_path ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3" />
              )}
              Roll back
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}



/* ── Section: Tutorial (YouTube embed + Kajabi) ───────────────────── */
function SectionTutorial({ draft, set }) {
  return (
    <div className="grid gap-4 max-w-3xl">
      <label className="grid gap-1">
        <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Tutorial URL (YouTube embed)</span>
        <input
          value={draft.tutorial_url || ""}
          onChange={(e) => set("tutorial_url", e.target.value)}
          placeholder="https://www.youtube.com/embed/…"
          data-testid="admin-module-tutorial-url"
          className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40"
        />
      </label>
      <label className="grid gap-1">
        <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Kajabi URL</span>
        <input
          value={draft.kajabi_url || ""}
          onChange={(e) => set("kajabi_url", e.target.value)}
          placeholder="https://cpginstitute.kajabi.com/…"
          data-testid="admin-module-kajabi-url"
          className="rounded-lg border border-shell-border px-3 py-2 text-sm focus:outline-none focus:border-primary/40"
        />
      </label>
    </div>
  );
}

/* ── Section: Worksheets attachment ────────────────────────────────── */
function SectionWorksheets({ moduleSlug, worksheets, onToggle, onReload }) {
  const [q, setQ] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  const filtered = useMemo(() => {
    if (!q.trim()) return worksheets;
    const needle = q.toLowerCase();
    return worksheets.filter((w) =>
      (w.title || "").toLowerCase().includes(needle) ||
      (w.original_filename || "").toLowerCase().includes(needle)
    );
  }, [q, worksheets]);

  const attached = worksheets.filter((w) => (w.module_slugs || []).includes(moduleSlug));

  // Drop files → bulk upload with this module_slug already attached, so the
  // dropped worksheets show up as attached the moment they land.
  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (!files.length || uploading) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("module_slugs", moduleSlug);
      const r = await api.post("/admin/worksheets/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${r.data?.uploaded || files.length} worksheet(s) and attached to this module`);
      await onReload?.();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="grid gap-4 max-w-3xl"
      onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      data-testid="admin-module-ws-dropzone"
    >
      <div className={"rounded-xl border-2 border-dashed p-4 transition-colors " +
        (dragOver
          ? "border-primary bg-primary/5"
          : "border-primary/40 bg-primary/5")
      }>
        <p className="text-[11px] uppercase tracking-widest font-bold text-primary flex items-center gap-1">
          <Upload className="w-3 h-3" />
          {uploading ? "Uploading…" : dragOver ? "Drop to upload + attach" : `${attached.length} attached to this module`}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Drag worksheet files anywhere in this panel to upload them and auto-attach to <span className="font-semibold">{moduleSlug}</span>.
          Or tick boxes below to attach worksheets that already exist in the shared catalog.
        </p>
      </div>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search worksheets…"
          className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-shell-border bg-white focus:outline-none focus:border-primary/40"
          data-testid="admin-module-ws-search"
        />
      </div>
      <ul className="max-h-[50vh] overflow-auto border border-shell-border rounded-lg divide-y divide-shell-border" data-testid="admin-module-ws-list">
        {filtered.length === 0 && (
          <li className="p-4 text-center text-sm text-muted-foreground">
            {worksheets.length === 0 ? "No worksheets in the catalog yet — drop files above to add some." : "No matches."}
          </li>
        )}
        {filtered.map((w) => {
          const on = (w.module_slugs || []).includes(moduleSlug);
          return (
            <li key={w.id} className="flex items-start gap-3 p-3 hover:bg-muted/40">
              <input
                type="checkbox"
                checked={on}
                onChange={() => onToggle(w)}
                data-testid={`admin-module-ws-toggle-${w.id}`}
                className="mt-1 accent-primary"
              />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{w.title}</p>
                <p className="text-[11px] text-muted-foreground truncate">{w.original_filename}</p>
              </div>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/worksheets/${w.id}/file`}
                target="_blank" rel="noreferrer"
                data-testid={`admin-module-ws-open-${w.id}`}
                className="text-xs text-muted-foreground hover:text-primary flex-shrink-0"
              >
                Open
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ── Section: Tools attachment ────────────────────────────────────── */
function SectionTools({ tools, selected, onToggle }) {
  return (
    <div className="grid gap-4 max-w-3xl">
      <div className="rounded-xl bg-primary/5 border border-primary/20 p-4">
        <p className="text-[11px] uppercase tracking-widest font-bold text-primary">
          {selected.length} tool{selected.length === 1 ? "" : "s"} attached
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Pick which financial planning tools appear at the bottom of this module's page.
        </p>
      </div>
      <ul className="border border-shell-border rounded-lg divide-y divide-shell-border" data-testid="admin-module-tools-list">
        {tools.length === 0 && (
          <li className="p-4 text-center text-sm text-muted-foreground">No tools in the catalog yet.</li>
        )}
        {tools.map((t) => {
          const on = selected.includes(t.slug);
          return (
            <li key={t.slug} className="flex items-start gap-3 p-3 hover:bg-muted/40">
              <input
                type="checkbox"
                checked={on}
                onChange={() => onToggle(t.slug)}
                data-testid={`admin-module-tool-toggle-${t.slug}`}
                className="mt-1 accent-primary"
              />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{t.name}</p>
                <p className="text-[11px] text-muted-foreground">{t.description}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
